class BlogLoadMore extends HTMLElement {
    constructor() {
        super();
    }

    connectedCallback() {
        this.initLoadMore();
        this.initBackToTop();
    }

    initBackToTop() {
        const btn = document.getElementById('blg-BackToTop');
        const articlesList = document.querySelector('.art-List_Items');
        if (!btn || !articlesList) return;

        // Keep fixed positioning reliable on iPad (avoid grid/flex containing blocks)
        if (btn.parentElement !== document.body) {
            document.body.appendChild(btn);
        }

        const TRIGGER_INDEX = 6; // Show when user has scrolled past ~6-7 articles

        const updateVisibility = () => {
            const items = articlesList.querySelectorAll('[data-blog-article]');
            const trigger = items[TRIGGER_INDEX];
            if (!trigger) {
                btn.classList.remove('is-visible');
                btn.setAttribute('aria-hidden', 'true');
                return;
            }
            const rect = trigger.getBoundingClientRect();
            const show = rect.top < window.innerHeight * 0.6;
            btn.classList.toggle('is-visible', show);
            btn.setAttribute('aria-hidden', show ? 'false' : 'true');
        };

        window.addEventListener('scroll', updateVisibility, { passive: true });
        document.addEventListener('blog:articlesAdded', updateVisibility);

        btn.addEventListener('click', () => {
            window.scrollTo({ top: 0, behavior: 'smooth' });
        });

        updateVisibility();
    }

    initLoadMore() {
        const loadMoreContainer = document.querySelector('.blg-LoadMore');
        if (!loadMoreContainer) return;

        const spinnerSpan = loadMoreContainer.querySelector('.blg-LoadMore_Spinner');
        const articlesList = document.querySelector('.art-List_Items');

        let currentPage = parseInt(loadMoreContainer.dataset.currentPage) || 1;
        const totalPages = parseInt(loadMoreContainer.dataset.totalPages) || 1;
        let blogUrl = loadMoreContainer.dataset.blogUrl || "";
        let isLoading = false;
        let hasNotifiedComplete = false;

        const currentTag = window.location.pathname.includes('/tagged/')
            ? window.location.pathname.split('/tagged/')[1].split('?')[0]
            : "";
        // When filtering by our categories, fetch all articles and let client-side filter handle it
        const useClientFilter = currentTag && /^(category-|roomtype-)([\w-]+\+?)+$/.test(currentTag);

        const fetchAndInjectBaseArticles = () => {
            if (!useClientFilter || !articlesList) return Promise.resolve();
            const initialCount = articlesList.querySelectorAll('[data-blog-article]').length;
            if (initialCount > 0) return Promise.resolve();
            const baseUrl = blogUrl.endsWith('/') ? blogUrl.slice(0, -1) : blogUrl;
            return fetch(baseUrl + '?page=1')
                .then(r => r.text())
                .then(html => {
                    const doc = new DOMParser().parseFromString(html, "text/html");
                    const newContainer = doc.querySelector(".art-List_Items");
                    if (newContainer) {
                        newContainer.querySelectorAll(".art-List_Item").forEach(a => articlesList.appendChild(a.cloneNode(true)));
                        document.dispatchEvent(new CustomEvent('blog:articlesAdded', { detail: { count: articlesList.querySelectorAll('[data-blog-article]').length } }));
                    }
                })
                .catch(() => {});
        };

        const updateLoadingState = () => {
            if (currentPage >= totalPages) {
                if (spinnerSpan) spinnerSpan.style.display = 'none';
                loadMoreContainer.style.display = 'none';
            }
        };

        const notifyAllLoaded = () => {
            if (hasNotifiedComplete) return;
            hasNotifiedComplete = true;

            const total = articlesList
                ? articlesList.querySelectorAll('[data-blog-article]').length
                : 0;

            // Update all category counts now that all articles are loaded
            this.updateAllCounts(total);

            document.dispatchEvent(
                new CustomEvent('blog:allArticlesLoaded', {
                    detail: { total }
                })
            );
        };

        const loadMoreArticles = (done) => {
            if (isLoading || currentPage >= totalPages) {
                if (done) done();
                if (currentPage >= totalPages) notifyAllLoaded();
                return;
            }

            isLoading = true;
            if (spinnerSpan) spinnerSpan.style.display = 'none';

            let nextPage = currentPage + 1;
            let url = blogUrl;

            if (url.endsWith('/')) url = url.slice(0, -1);
            if (currentTag && !useClientFilter) url += "/tagged/" + currentTag;
            url += "?page=" + nextPage;

            fetch(url)
                .then(r => r.text())
                .then(html => {
                    const parser = new DOMParser();
                    const doc = parser.parseFromString(html, "text/html");
                    const newContainer = doc.querySelector(".art-List_Items");

                    if (!newContainer)
                        throw new Error("No .art-List_Items found");

                    const newArticles = newContainer.querySelectorAll(".art-List_Item");
                    if (!newArticles.length)
                        throw new Error("No articles found");

                    newArticles.forEach(a =>
                        articlesList.appendChild(a.cloneNode(true))
                    );

                    // Notify that new articles were added so filters can be re-applied
                    document.dispatchEvent(
                        new CustomEvent('blog:articlesAdded', {
                            detail: { count: newArticles.length }
                        })
                    );

                    currentPage = nextPage;
                    updateLoadingState();
                    if (currentPage >= totalPages) notifyAllLoaded();

                    if (done) done();
                })
                .catch(err => {
                    if (done) done();
                })
                .finally(() => {
                    isLoading = false;
                    if (spinnerSpan) spinnerSpan.style.display = 'none';
                    // Don't update counts here - wait until all articles are loaded
                });
        };

        const loadAllImmediately = () => {
            if (currentPage >= totalPages) {
                updateLoadingState();
                notifyAllLoaded();
                return;
            }
            loadMoreArticles(() => setTimeout(loadAllImmediately, 50));
        };

        fetchAndInjectBaseArticles().then(() => {
            if (useClientFilter && articlesList && articlesList.querySelectorAll('[data-blog-article]').length > 0) {
                currentPage = 1;
                loadMoreContainer.dataset.totalPages = '20';
            }
            loadAllImmediately();
        });
        updateLoadingState();
        if (currentPage >= totalPages) notifyAllLoaded();
    }

    updateAllCounts(total) {
        // Update "All articles" count
        const allArticlesElements = document.querySelectorAll('[data-all-articles] .blg-Navigation_Count');
        allArticlesElements.forEach(el => {
            el.classList.remove('blg-Navigation_Count--loading');
            el.textContent = total;
        });

        const articles = document.querySelectorAll('[data-blog-article]');

        const articleMatchesCategory = (tagsAttr, catClean, isHowTo) => {
            const tags = (tagsAttr || '').split('|').map(t => t.trim().toLowerCase());
            for (let i = 0; i < tags.length; i++) {
                const t = tags[i];
                if (!t) continue;
                if (isHowTo) {
                    if (t.indexOf('how to') !== -1) return true;
                } else {
                    const catSpace = catClean.replace(/-/g, ' ');
                    if (t === catClean || t === catSpace || t === 'category:' + catClean || t === 'category: ' + catSpace ||
                        t === 'category-' + catClean || t === 'roomtype-' + catClean) return true;
                }
            }
            return false;
        };

        // Update category counts (count articles, not tag occurrences - must match filter logic)
        const categoryElements = document.querySelectorAll('[data-category-tag]');
        categoryElements.forEach(el => {
            const categoryTag = el.getAttribute('data-category-tag').toLowerCase();
            const countSpan = el.querySelector('.blg-Navigation_Count');
            if (!countSpan) return;

            const catClean = categoryTag.replace('category-', '').replace('roomtype-', '');
            const isHowTo = (catClean === 'how-to' || categoryTag === 'category-how-to');
            let count = 0;

            articles.forEach(article => {
                const tagsAttr = article.getAttribute('data-article-tags') || '';
                if (articleMatchesCategory(tagsAttr, catClean, isHowTo)) count++;
            });

            countSpan.classList.remove('blg-Navigation_Count--loading');
            countSpan.textContent = count;
        });
    }
}
customElements.define("blog-load-more", BlogLoadMore);